/**
 * 声劇ステージ（/stage）— STAGE_SPEC §5 画面仕様
 * β=2機能: STG-01 台本ライブラリ / STG-02 キャスティング募集。非同期上演＋提出順の自動連結再生（決定9）。
 * 成立=募集主受入＋全枠充足（決定10）。裏方枠は「先行募集」ラベル必須（決定12）。
 * サロン参照台本は読み取り専用＋原作クレジット常時表示（決定11）。観客反応は作品化後postのみ（決定15）。
 */
import { FC, useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { api, StageScript, StageCasting, StageSlot, StageApplication, PlaybackTrack, SalonNote, CastingProgress } from '../services/api';
import { resolveAssetUrl } from '../hooks/useSiteAssets';
import { SubmissionModal } from '../components/SubmissionModal';
import { PulseButton } from '../components/ui/PulseButton';
import { Icon } from '../components/icons/Icon';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';
import { TownShell, TownLoading, TownError, TownEmpty, TownGoals, useTownUser, timeAgo } from '../components/town/townShared';

type Load = 'loading' | 'ready' | 'error';
const parseOrigin = (s: string | null): { space: string; item_id: string; version?: number } | null => {
  if (!s) return null; try { return JSON.parse(s); } catch { return null; }
};

/**
 * PIPE-02 原作クレジット「サロン発・原作: 〇〇」の常時表示（STAGE_SPEC §8）。
 * credited_author（author_id）を GET /api/public/users/:id で表示名へ解決する。
 * - 解決中はクレジット行のみスケルトン（**誤った作者名を先出ししない**／§5.4 partial）
 * - 解決できない場合は名前を捏造せず既定表示「原作者」にフォールバック（事実性①）
 */
const SalonCredit: FC<{ authorId: string | null; suffix?: string }> = ({ authorId, suffix = '' }) => {
  const [name, setName] = useState<string | null | undefined>(undefined); // undefined = 解決中
  useEffect(() => {
    let cancelled = false;
    if (!authorId) { setName(null); return; }
    api.getPublicUser(authorId).then(u => { if (!cancelled) setName(u?.display_name ?? null); });
    return () => { cancelled = true; };
  }, [authorId]);

  const cls = 'inline-flex items-center gap-1.5 text-[10px] font-bold text-role-script-text bg-role-script-soft border border-role-script rounded px-1.5 py-0.5';
  if (name === undefined) {
    return (
      <span className={cls} role="status" aria-label="原作クレジットを読み込み中">
        サロン発・原作:
        <span className="inline-block w-14 h-2.5 rounded bg-role-script/30 motion-safe:animate-pulse" aria-hidden />
      </span>
    );
  }
  return <span className={cls}>サロン発・原作: {name || '原作者'}{suffix}</span>;
};

// ══════════════════════════════════════════════════════════
// ロビー /stage
// ══════════════════════════════════════════════════════════
export const Stage: FC = () => {
  const [, setLocation] = useLocation();
  const { user, requireLogin, loginGate } = useTownUser();
  const { playSound } = useGamenics();
  const [castings, setCastings] = useState<StageCasting[]>([]);
  const [scripts, setScripts] = useState<StageScript[]>([]);
  const [state, setState] = useState<Load>('loading');
  const [composing, setComposing] = useState(false);

  const load = () => {
    setState('loading');
    Promise.all([api.stageListCastings(), api.stageListScripts()])
      .then(([c, s]) => { setCastings(c); setScripts(s); setState('ready'); })
      .catch(() => setState('error'));
  };
  useEffect(load, []);

  return (
    <TownShell role="voice" kicker="PERFORMERS' STAGE" title="声劇ステージ"
      actions={
        <div className="flex gap-2">
          <button className="btn btn-ghost btn-sm min-h-[44px]" onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/stage/board'); }}>掲示板</button>
          <button className="btn btn-ghost btn-sm min-h-[44px]" onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); requireLogin(() => setComposing(true)); }}>台本を掲げる</button>
        </div>
      }>
      <p className="text-ink-2 text-sm mb-6 max-w-2xl">台本を持ち寄り、声だけで幕を上げる。役を募り、声を持ち寄って、一座で一本の上演に。</p>

      {/* 稽古場（RT-01 / B-1）への導線。ナビ本数は増やさず本文に1箇所だけ置く */}
      <button
        onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/stage/rooms'); }}
        className="w-full max-w-2xl mb-8 flex items-center gap-3 text-left bg-role-voice-soft border-2 border-ink rounded-pop-md px-5 py-4 min-h-[44px] shadow-hard-sm transition-transform duration-150 hover:-translate-x-px hover:-translate-y-px active:translate-x-0.5 active:translate-y-0.5 active:shadow-none"
      >
        <Icon name="mic" size={22} className="text-role-voice-text shrink-0" />
        <span className="flex-1">
          <span className="font-display font-bold text-role-voice-text">稽古場で声を合わせる</span>
          <span className="block text-ink-2 text-xs mt-1">最大4名でその場で読み合わせできます。ここは稽古用なので、録音は残りません。</span>
        </span>
        <Icon name="arrow-right" size={18} className="text-ink-2 shrink-0" />
      </button>

      {user && <TownGoals role="voice" />}

      {state === 'loading' && <TownLoading />}
      {state === 'error' && <TownError message="ロビーを開けませんでした" onRetry={load} />}
      {state === 'ready' && (
        <>
          {/* 一座のようす（open/casting/ready を含むため「募集中」と断定しない。状態はバッジが示す） */}
          <h2 className="text-xs font-bold text-ink-2 mb-3 tracking-wide">一座のようす</h2>
          {castings.length === 0 ? (
            <TownEmpty title="まだ幕は上がっていません。最初の一座を組みませんか。">
              <button className="btn btn-primary min-h-[44px]" onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/stage'); document.getElementById('scripts')?.scrollIntoView({ behavior: 'smooth' }); }}>台本ライブラリを見る</button>
            </TownEmpty>
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 list-none p-0 m-0 mb-8">
              {castings.map(c => (
                <li key={c.id}>
                  <button onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation(`/stage/castings/${c.id}`); }}
                    className="pop-card w-full text-left p-5 hover:-translate-y-0.5 transition-transform min-h-[44px]">
                    <div className="flex items-center gap-2">
                      <span className="font-display font-bold text-ink flex-1">{c.title || '一座'}</span>
                      <StatusBadge status={c.status} />
                    </div>
                    <span className="block mt-2 text-[11px] text-ink-3 tabular-nums">{timeAgo(c.created_at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* 台本ライブラリ */}
          <h2 id="scripts" className="text-xs font-bold text-ink-2 mb-3 tracking-wide scroll-mt-20">新着の台本</h2>
          {scripts.length === 0 ? (
            <TownEmpty title="棚はまだ空です。あなたの台本を最初の一冊に。">
              <PulseButton isPrimary onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); requireLogin(() => setComposing(true)); }}>台本を掲げる</PulseButton>
            </TownEmpty>
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 list-none p-0 m-0">
              {scripts.map(s => <li key={s.id}><ScriptCard s={s} onOpen={() => setLocation(`/stage/scripts/${s.id}`)} /></li>)}
            </ul>
          )}
        </>
      )}

      {composing && <ScriptComposer user={user} onClose={() => setComposing(false)} onSaved={(id) => { setComposing(false); if (id) setLocation(`/stage/scripts/${id}`); else load(); }} />}
      {loginGate}
    </TownShell>
  );
};

const StatusBadge: FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { t: string; c: string }> = {
    open: { t: '募集中', c: 'bg-role-voice-soft text-role-voice-text border-role-voice' },
    casting: { t: '選考中', c: 'bg-role-voice-soft text-role-voice-text border-role-voice' },
    ready: { t: '成立', c: 'bg-sun text-ink border-ink' },
    performed: { t: '上演済み', c: 'bg-role-illustration-soft text-role-illustration-text border-role-illustration' },
    closed: { t: '終了', c: 'bg-cream-2 text-ink-2 border-ink/30' },
  };
  // 未知の状態を「募集中」と偽らない（事実性①）。判定できないときは中立表示に落とす
  const m = map[status] || { t: '状態不明', c: 'bg-cream-2 text-ink-2 border-ink/30' };
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${m.c}`}>{m.t}</span>;
};

const ScriptCard: FC<{ s: StageScript; onOpen: () => void }> = ({ s, onOpen }) => {
  const { playSound } = useGamenics();
  const origin = parseOrigin(s.origin_ref);
  return (
    <button onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); onOpen(); }}
      className="pop-card w-full text-left p-5 hover:-translate-y-0.5 transition-transform min-h-[44px]">
      <div className="flex items-center gap-2 mb-1">
        <span className="font-display font-bold text-ink flex-1">{s.title}</span>
        {s.published_post_id && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-role-voice-soft text-role-voice-text border border-role-voice">作品化済み</span>}
      </div>
      {/* PIPE-02 参照台本の原作クレジット常時表示（決定11） */}
      {origin?.space === 'salon' && (
        <span className="block mb-1"><SalonCredit authorId={s.credited_author} /></span>
      )}
      <p className="text-sm text-ink-2 line-clamp-2 whitespace-pre-wrap">{s.body}</p>
    </button>
  );
};

// ── 台本を掲げる（新規 or サロン参照インポート＝読み取り専用/決定11） ──
const ScriptComposer: FC<{ user: any; onClose: () => void; onSaved: (id?: string) => void }> = ({ onClose, onSaved }) => {
  const { playSound } = useGamenics();
  const [mode, setMode] = useState<'new' | 'ref'>('new');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [notes, setNotes] = useState<SalonNote[] | null>(null);
  const [picked, setPicked] = useState<SalonNote | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadNotes = () => { api.salonListNotes().then(rows => setNotes(rows.filter(n => n.draft_text))); };

  const save = async () => {
    if (!title.trim() || !body.trim()) { setError('タイトルと本文が必要です'); return; }
    const payload: any = { title, body };
    if (mode === 'ref' && picked) {
      // 参照インポート: 読み取り専用・原作クレジット（決定11）。参照時点の version を保持
      payload.origin_ref = { space: 'salon', item_id: picked.id, version: picked.version };
      payload.credited_author = picked.author_id;
    }
    const r = await api.stageCreateScript(payload);
    if (r.success) { playSound(GamenicsEvent.SUBMISSION_COMPLETE); onSaved(r.id); }
    else setError(r.error || 'うまく掲げられませんでした');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface border-2 border-ink rounded-pop p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-display font-bold mb-4">台本を掲げる</h2>
        <div className="flex gap-2 mb-4">
          <button onClick={() => { setMode('new'); }} aria-pressed={mode === 'new'} className={`btn btn-sm flex-1 min-h-[44px] ${mode === 'new' ? 'btn-primary' : 'btn-ghost'}`}>自分で書く</button>
          <button onClick={() => { setMode('ref'); if (!notes) loadNotes(); }} aria-pressed={mode === 'ref'} className={`btn btn-sm flex-1 min-h-[44px] ${mode === 'ref' ? 'btn-primary' : 'btn-ghost'}`}>サロンの台本を参照</button>
        </div>

        {mode === 'ref' && (
          <div className="mb-4">
            {!picked ? (
              <>
                <p className="text-[11px] text-ink-3 mb-2">参照した台本は読み取り専用で、原作クレジットが常時表示されます（決定11）。</p>
                {notes === null ? <TownLoading label="サロンの台本を読み込み中" /> : notes.length === 0 ? (
                  <p className="text-sm text-ink-3">参照できる公開台本がまだありません。</p>
                ) : (
                  <ul className="flex flex-col gap-2 max-h-40 overflow-y-auto list-none p-0 m-0">
                    {notes.map(n => (
                      <li key={n.id}>
                        <button onClick={() => { setPicked(n); setTitle(n.title || '無題の台本'); setBody(n.draft_text || ''); }}
                          className="w-full text-left px-3 py-2 min-h-[44px] rounded-pop-sm border-2 border-ink bg-cream hover:bg-sun">
                          <span className="block font-bold text-sm text-ink">{n.title || '無題'}</span>
                          <span className="block text-[11px] text-ink-2 line-clamp-1">{n.draft_text}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <p className="text-[11px] text-role-script-text bg-role-script-soft border border-role-script rounded px-2 py-1">サロン発・原作: {picked.author_id.slice(0, 10)}（本文は読み取り専用）</p>
            )}
          </div>
        )}

        <label className="block text-sm text-ink-2 mb-1">タイトル</label>
        <input value={title} onChange={e => setTitle(e.target.value)} className="pop-field w-full mb-3" />
        <label className="block text-sm text-ink-2 mb-1">本文（ト書き含む）</label>
        <textarea value={body} onChange={e => setBody(e.target.value)} readOnly={mode === 'ref' && !!picked} rows={7}
          className={`pop-field w-full mb-2 resize-none ${mode === 'ref' && picked ? 'opacity-70' : ''}`} placeholder="台本の本文" />
        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
        <div className="flex gap-3 mt-2">
          <button className="btn btn-ghost flex-1 min-h-[44px]" onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); onClose(); }}>やっぱりやめる</button>
          <PulseButton isPrimary className="flex-1" onClick={save}>台本を掲げる</PulseButton>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════
// 台本詳細 /stage/scripts/:id （STG-01 + 募集作成）
// ══════════════════════════════════════════════════════════
export const StageScriptDetail = ({ params }: any) => {
  const id: string = params?.id;
  const [, setLocation] = useLocation();
  const { requireLogin, loginGate } = useTownUser();
  const { playSound } = useGamenics();
  const [script, setScript] = useState<(StageScript & { castings?: StageCasting[] }) | null>(null);
  const [state, setState] = useState<Load>('loading');
  const [recruiting, setRecruiting] = useState(false);

  const load = () => {
    setState('loading');
    api.stageGetScript(id).then(s => { if (!s) { setState('error'); return; } setScript(s); setState('ready'); }).catch(() => setState('error'));
  };
  useEffect(load, [id]);

  const origin = script ? parseOrigin(script.origin_ref) : null;

  return (
    <TownShell role="voice" kicker="SCRIPT" title="台本">
      {state === 'loading' && <TownLoading />}
      {state === 'error' && <TownError message="台本を開けませんでした" onRetry={load} />}
      {state === 'ready' && script && (
        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-6">
          <div>
            <h2 className="font-display font-bold text-lg text-ink mb-2">{script.title}</h2>
            {origin?.space === 'salon' && (
              <p className="mb-3"><SalonCredit authorId={script.credited_author} suffix="（読み取り専用）" /></p>
            )}
            <div className="pop-card p-5 text-ink whitespace-pre-wrap leading-relaxed text-[15px]">{script.body}</div>
          </div>
          <aside>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-ink-2 tracking-wide">この台本の一座</h3>
              <button className="btn btn-primary btn-sm min-h-[44px]" onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); requireLogin(() => setRecruiting(true)); }}>仲間を募る</button>
            </div>
            {(script.castings || []).length === 0 ? (
              <p className="text-sm text-ink-3">まだ募集はありません。最初の一座を組みませんか。</p>
            ) : (
              <ul className="flex flex-col gap-2 list-none p-0 m-0">
                {script.castings!.map(c => (
                  <li key={c.id}>
                    <button onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation(`/stage/castings/${c.id}`); }}
                      className="pop-card w-full text-left p-3 flex items-center gap-2 min-h-[44px]">
                      <span className="flex-1 font-bold text-sm text-ink">{c.title || '一座'}</span>
                      <StatusBadge status={c.status} />
                      <Icon name="arrow-right" size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      )}

      {recruiting && script && <CastingComposer scriptId={script.id} onClose={() => setRecruiting(false)} onSaved={(cid) => { setRecruiting(false); if (cid) setLocation(`/stage/castings/${cid}`); else load(); }} />}
      {loginGate}
    </TownShell>
  );
};

// ── 募集を作成（役・裏方の枠。裏方は「先行募集」＝preview/決定12） ──
const CastingComposer: FC<{ scriptId: string; onClose: () => void; onSaved: (id?: string) => void }> = ({ scriptId, onClose, onSaved }) => {
  const { playSound } = useGamenics();
  const [title, setTitle] = useState('');
  const [slots, setSlots] = useState<StageSlot[]>([{ role_id: 'r1', role_name: '', kind: 'cast', capacity: 1 }]);
  const [error, setError] = useState<string | null>(null);

  const addSlot = (kind: 'cast' | 'crew') =>
    setSlots(s => [...s, { role_id: `r${s.length + 1}`, role_name: '', kind, capacity: 1, preview: kind === 'crew' }]);
  const setSlot = (i: number, patch: Partial<StageSlot>) => setSlots(s => s.map((x, j) => j === i ? { ...x, ...patch } : x));
  const removeSlot = (i: number) => setSlots(s => s.filter((_, j) => j !== i));

  const save = async () => {
    const clean = slots.filter(s => (s.role_name || '').trim());
    if (clean.length === 0) { setError('役の名前を1つ以上入れてください'); return; }
    const r = await api.stageCreateCasting({ script_id: scriptId, title: title || undefined, slots: clean });
    if (r.success) { playSound(GamenicsEvent.SUBMISSION_COMPLETE); onSaved(r.id); }
    else setError(r.error || '募集を作成できませんでした');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface border-2 border-ink rounded-pop p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-display font-bold mb-4">仲間を募る</h2>
        <label className="block text-sm text-ink-2 mb-1">一座の名前（任意）</label>
        <input value={title} onChange={e => setTitle(e.target.value)} className="pop-field w-full mb-4" placeholder="◯◯一座" />
        <p className="text-sm text-ink-2 mb-2 font-bold">募集する枠</p>
        <ul className="flex flex-col gap-2 mb-3 list-none p-0 m-0">
          {slots.map((s, i) => (
            <li key={i} className="flex items-center gap-2">
              <input value={s.role_name} onChange={e => setSlot(i, { role_name: e.target.value })} placeholder={s.kind === 'crew' ? '裏方の役割（BGM/背景 等）' : '役名'} className="pop-field flex-1" />
              {s.kind === 'crew' && <span className="text-[10px] font-bold text-role-illustration-text bg-role-illustration-soft border border-role-illustration rounded px-1.5 py-0.5">先行募集</span>}
              <input type="number" min={1} max={9} value={s.capacity} onChange={e => setSlot(i, { capacity: Number(e.target.value) })} className="pop-field w-14" aria-label="枠数" />
              {slots.length > 1 && <button onClick={() => removeSlot(i)} className="text-ink-3 min-h-[44px]" aria-label="この枠を消す"><Icon name="x" size={16} /></button>}
            </li>
          ))}
        </ul>
        {/* 裏方は「先行募集」ラベル必須（決定12）— 出来ない作業を出来ると見せない */}
        <div className="flex gap-2 mb-3">
          <button onClick={() => addSlot('cast')} className="btn btn-ghost btn-sm min-h-[44px]"><Icon name="plus" size={14} /> 役を足す</button>
          <button onClick={() => addSlot('crew')} className="btn btn-ghost btn-sm min-h-[44px]"><Icon name="plus" size={14} /> 裏方を足す</button>
        </div>
        <p className="text-[11px] text-ink-3 mb-3">※裏方（音響・照明）はPhase 3で本格稼働。現在は素材提供（BGM/SE・背景絵）と準備メンバーの募集です（先行募集）。</p>
        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
        <div className="flex gap-3">
          <button className="btn btn-ghost flex-1 min-h-[44px]" onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); onClose(); }}>やっぱりやめる</button>
          <PulseButton isPrimary className="flex-1" onClick={save}>この枠で募る</PulseButton>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════
// 募集詳細 /stage/castings/:id （STG-02 + 連結再生 + PIPE-01）
// ══════════════════════════════════════════════════════════
export const StageCastingDetail = ({ params }: any) => {
  const id: string = params?.id;
  const { user, requireLogin, loginGate } = useTownUser();
  const { playSound } = useGamenics();
  const [casting, setCasting] = useState<(StageCasting & { slots: StageSlot[]; applications: StageApplication[]; progress?: CastingProgress }) | null>(null);
  const [state, setState] = useState<Load>('loading');
  const [tracks, setTracks] = useState<PlaybackTrack[]>([]);
  const [submitFor, setSubmitFor] = useState<StageApplication | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);

  const load = () => {
    setState('loading');
    api.stageGetCasting(id).then(c => { if (!c) { setState('error'); return; } setCasting(c); setState('ready'); }).catch(() => setState('error'));
    api.stageGetPlayback(id).then(p => setTracks(p.tracks));
  };
  useEffect(load, [id]);

  const isHost = !!user && !!casting && user.id === casting.host_id;
  const slots: StageSlot[] = casting?.slots || [];
  const apps: StageApplication[] = casting?.applications || [];
  const acceptedCount = (roleId: string) => apps.filter(a => a.role_id === roleId && a.status === 'accepted').length;

  const apply = (roleId: string) => requireLogin(async () => {
    const r = await api.stageApply(id, roleId);
    if (r.success) { playSound(GamenicsEvent.TAP_CONFIRM); load(); } else alert(r.error || 'すでに応募済みかもしれません');
  });
  const accept = async (appId: string) => {
    const r = await api.stageAccept(id, appId);
    if (r.success) { playSound(GamenicsEvent.TAP_CONFIRM); load(); }
  };
  const myAcceptedApp = apps.find(a => a.applicant_id === user?.id && a.status === 'accepted' && !a.submission_post_id);

  return (
    <TownShell role="voice" kicker="CASTING" title={casting?.title || '一座'}>
      {state === 'loading' && <TownLoading />}
      {state === 'error' && <TownError message="募集を開けませんでした" onRetry={load} />}
      {state === 'ready' && casting && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6">
          {/* 枠と応募 */}
          <div>
            <div className="flex items-center gap-2 mb-3"><StatusBadge status={casting.status} /></div>

            {/* STG-03: 一座の進行状態（決定21）。すべて都度集計の実数で、架空の数値は出さない（事実性①） */}
            {casting.progress && (
              <div className="pop-card p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-ink-2">一座の進み具合</span>
                  <span className="text-sm font-display font-bold text-ink tabular-nums">
                    {casting.progress.filled}<span className="text-ink-3 font-normal"> / {casting.progress.total_capacity}</span>
                  </span>
                </div>
                <div
                  className="h-2.5 rounded-full bg-cream-2 border border-ink/20 overflow-hidden"
                  role="progressbar"
                  aria-valuenow={casting.progress.filled}
                  aria-valuemin={0}
                  aria-valuemax={casting.progress.total_capacity}
                  aria-label="役の充足状況"
                >
                  <div
                    className="h-full bg-role-voice motion-safe:transition-all motion-safe:duration-300"
                    style={{ width: `${casting.progress.total_capacity > 0 ? Math.min(100, (casting.progress.filled / casting.progress.total_capacity) * 100) : 0}%` }}
                  />
                </div>
                <p className="text-[11px] text-ink-2 mt-2">
                  受け入れ済み {casting.progress.filled} 人・応募中 {casting.progress.applied} 人
                  {casting.progress.status === 'ready' && <span className="text-role-voice-text font-bold">／一座は成立しています</span>}
                </p>
              </div>
            )}

            <ul className="flex flex-col gap-3 list-none p-0 m-0">
              {slots.map(s => {
                const roleApps = apps.filter(a => a.role_id === s.role_id);
                const filled = acceptedCount(s.role_id);
                const slotFull = filled >= (s.capacity ?? 1);
                return (
                  <li key={s.role_id} className="pop-card p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-ink flex-1">{s.role_name || '役'}</span>
                      {s.kind === 'crew' && <span className="text-[10px] font-bold text-role-illustration-text bg-role-illustration-soft border border-role-illustration rounded px-1.5 py-0.5">先行募集</span>}
                      <span className="text-[11px] tabular-nums text-ink-2">{filled}/{s.capacity ?? 1}</span>
                    </div>
                    {s.kind === 'crew' && <p className="text-[11px] text-ink-3 mb-2">音響・照明はPhase 3で本格稼働。現在は素材提供・準備メンバー募集です。</p>}
                    {/* 応募一覧（募集主は受け入れ可） */}
                    {roleApps.length > 0 && (
                      <ul className="flex flex-col gap-1 mb-2 list-none p-0 m-0">
                        {roleApps.map(a => (
                          <li key={a.id} className="flex items-center gap-2 text-[12px]">
                            <span className="flex-1 text-ink-2">{a.applicant_id.slice(0, 8)}…</span>
                            <span className="text-ink-3">{a.status === 'accepted' ? '受入済み' : a.submission_post_id ? '録音済み' : '応募中'}</span>
                            {isHost && a.status === 'applied' && (
                              <button className="btn btn-ghost btn-sm min-h-[36px]" onClick={() => accept(a.id)}>受け入れる</button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    {/* 応募ボタンは「募集を受付中」かつ「枠に空きあり」のときだけ出す。
                        満席・成立済みで押せてしまう→サーバーが無検証で受理、のロジック破綻を修正（2026-08-14）。
                        サーバー側にも同じガード（CASTING_CLOSED / SLOT_FULL）を入れてある */}
                    {user?.id !== casting.host_id && (casting.status === 'open' || casting.status === 'casting') && (
                      slotFull ? (
                        <p className="text-[11px] text-ink-3 text-center py-2">この役は定員に達しています</p>
                      ) : (
                        <button className="btn btn-primary btn-sm w-full min-h-[44px]" onClick={() => apply(s.role_id)}>この役をやりたい</button>
                      )
                    )}
                  </li>
                );
              })}
            </ul>

            {/* 成立=募集主受入＋全枠充足（決定10）。録音提出/作品化 */}
            {myAcceptedApp && (
              <div className="mt-4">
                <PulseButton isPrimary onClick={() => setSubmitFor(myAcceptedApp)}>声を吹き込む</PulseButton>
              </div>
            )}
            {isHost && (casting.status === 'ready' || casting.status === 'performed') && (
              <div className="mt-4">
                <PulseButton isPrimary onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setPublishOpen(true); }}>作品として届ける！</PulseButton>
                <p className="text-[11px] text-ink-3 mt-1">今ある声で届けられます（未提出の役は空席のまま）。</p>
              </div>
            )}
          </div>

          {/* 連結再生プレイヤー（決定9） */}
          <aside>
            <h3 className="text-xs font-bold text-ink-2 mb-3 tracking-wide">通しで聴く（連結再生）</h3>
            <ConnectedPlayer tracks={tracks} onReload={() => api.stageGetPlayback(id).then(p => setTracks(p.tracks))} />
          </aside>
        </div>
      )}

      {/* 録音提出: 音声postを作成→application に紐付け */}
      {submitFor && (
        <SubmissionModal
          postId="genesis" role="voice"
          onClose={() => setSubmitFor(null)}
          onSuccess={async (postId) => {
            if (postId) await api.stageSubmitRecording(id, submitFor.id, postId);
            setSubmitFor(null); load();
          }}
        />
      )}
      {/* 作品化（募集主）: 上演postを作成→published_post_id 記録（PIPE-01） */}
      {publishOpen && casting && (
        <SubmissionModal
          postId="genesis" role="voice"
          originRef={{ space: 'stage', item_id: casting.id }}
          onClose={() => setPublishOpen(false)}
          onSuccess={async (postId) => { if (postId) await api.stagePublish(id, postId); setPublishOpen(false); load(); }}
        />
      )}
      {loginGate}
    </TownShell>
  );
};

// ══════════════════════════════════════════════════════════
// 連結再生プレイヤー（提出順に自動連続再生・未提出役は空席／決定9）
// ══════════════════════════════════════════════════════════
const ConnectedPlayer: FC<{ tracks: PlaybackTrack[]; onReload: () => void }> = ({ tracks, onReload }) => {
  const { playSound } = useGamenics();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [current, setCurrent] = useState<number>(-1);
  const playable = tracks.map((t, i) => ({ t, i })).filter(x => !x.t.empty && x.t.url);

  const playFrom = (idx: number) => {
    const list = playable;
    const startPos = list.findIndex(x => x.i >= idx);
    if (startPos === -1) return;
    setCurrent(list[startPos].i);
  };
  useEffect(() => {
    if (current < 0) return;
    const track = tracks[current];
    const a = audioRef.current;
    if (!a || !track?.url) return;
    a.src = resolveAssetUrl(track.url);
    a.play().catch(() => {});
  }, [current]);

  const onEnded = () => {
    // 次の提出済みトラックへ自動連結（未提出役は空席として飛ばす）
    const next = playable.find(x => x.i > current);
    if (next) setCurrent(next.i); else setCurrent(-1);
  };

  if (tracks.length === 0) {
    return (
      <div className="pop-card p-4 text-center">
        <p className="text-sm text-ink-3 mb-2">まだ声が集まっていません。</p>
        <button className="btn btn-ghost btn-sm min-h-[44px]" onClick={onReload}>最新の状態にする</button>
      </div>
    );
  }
  return (
    <div className="pop-card p-4">
      <audio ref={audioRef} onEnded={onEnded} />
      <div className="flex gap-2 mb-3">
        <PulseButton isPrimary onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); playFrom(0); }} disabled={playable.length === 0}>
          <Icon name="play" size={14} /> 通しで聴く
        </PulseButton>
        {current >= 0 && (
          <button className="btn btn-ghost btn-sm min-h-[44px]" onClick={() => { audioRef.current?.pause(); setCurrent(-1); }}>
            <Icon name="pause" size={14} /> 止める
          </button>
        )}
      </div>
      <ul className="flex flex-col gap-1 list-none p-0 m-0">
        {tracks.map((t, i) => (
          <li key={i} className={`flex items-center gap-2 px-2 py-1.5 rounded-pop-sm text-sm ${i === current ? 'bg-role-voice-soft' : ''}`}>
            <span className={`w-2 h-2 rounded-full ${t.empty ? 'bg-ink/20' : 'bg-role-voice'}`} aria-hidden />
            <span className="flex-1 text-ink-2">{t.role_name || '役'}</span>
            {t.empty ? (
              <span className="text-[11px] text-ink-3">空席（声を待っています）</span>
            ) : (
              <button className={`text-[11px] font-bold min-h-[36px] ${i === current ? 'text-role-voice-text' : 'text-ink-2 underline'}`} onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setCurrent(i); }}>
                このセリフから
              </button>
            )}
          </li>
        ))}
      </ul>
      {playable.length === 0 && <p className="text-[11px] text-ink-3 mt-2">まだ録音が1件もありません。連結再生はできません。</p>}
    </div>
  );
};
